import { client } from "../api/client.js";
import { setPageContext } from "../utils/pageContext.js";
import { renderPreview } from "../ui/previews.js";
import { renderMediaZone, bindMediaZoneEvents } from "../ui/mediaZone.js";
import { formatDateMDY, formatDateTimeMDY12h, toDatetimeLocalValue } from "../utils/date.js";
import { renderPageHeader } from "../ui/pageHeader.js";
import { toast } from '../ui/toast.js';
import { confirmModal } from '../ui/confirmModal.js';
import { FailedPostsDrawer } from '../ui/failedPostsDrawer.js';
import { escapeHtml } from '../ui/escapeHtml.js';
import { openGenerateMonthWizard } from '../ui/generateMonthWizard.js';
import { openMediaLibrary } from './create-social.js';
import { renderAiAssistToolbar, handleAiAssistClick } from '../ui/aiAssistToolbar.js';
import { renderCharCounter, updateCharCounter } from '../ui/charCounter.js';

function isBlogItem(item) {
  if (!item) return false;
  return item._source === 'blog_posts'
      || item.type === 'blog_post'
      || item.type === 'blog'
      || item.platform === 'blog'
      || item.platform === 'website';
}

export class LibraryPage {
  constructor() {
    this.items = [];
    this.loading = false;
    this.error = null;
    // type/status accept "all" OR an array of keys (multi-select).
    // Filters persist across navigation via sessionStorage.
    this.filters = this._loadFiltersFromStorage() || { status: "all", type: "all" };
    // Date-range filter: preset + optional custom from/to (ISO strings).
    // Persists across navigation like the other filters.
    this.dateFilter = this.filters.dateFilter || { preset: 'all', from: null, to: null };
    this.platformFilterOpen = false;
    this.statusFilterOpen = false;
    this.dateFilterOpen = false;
    this.customDateDraft = { from: '', to: '' };
    // Date sort direction: 'desc' (newest first, default) or 'asc' (oldest first)
    this.dateSort = this._loadFiltersFromStorage()?.dateSort || 'desc';
    this.viewMode = 'list';
    this.showCampaignPosts = true; // BF-3: toggle campaign posts visibility

    // State
    this.selectedIds = new Set();
    this.repurposing = false;
    this.selectedItemId = null;
    this.drawerOpen = false;
    this.saveState = "idle"; // 'idle' | 'saving' | 'saved'
    this.draftText = "";
    this.draftTitle = "";
    this.draftScheduledAt = "";
    this.draftMedia = [];
  }

  async mount(container) {
    this.container = container;
    this.loading = true;
    this.render();
    this.loadLibrary();
  }

  _parseMedia(items) {
    return items.map(item => {
      if (item.media_json) {
        try {
          const parsed = JSON.parse(item.media_json);
          // Normalize to an array. Historical shapes:
          //   - array of { url, type, ... }               (modern, expected)
          //   - { urls: [...] }                          (legacy MCP create_draft)
          //   - { url: '...' } or any other object        (stray old drafts)
          //   - bare string URL                           (very old rows)
          if (Array.isArray(parsed)) {
            item.media = parsed;
          } else if (parsed && Array.isArray(parsed.urls)) {
            item.media = parsed.urls.map(u => typeof u === 'string' ? { url: u, type: 'image' } : u);
          } else if (parsed && typeof parsed === 'object' && parsed.url) {
            item.media = [parsed];
          } else if (typeof parsed === 'string') {
            item.media = [{ url: parsed, type: 'image' }];
          } else {
            item.media = [];
          }
        } catch { item.media = []; }
      }
      return item;
    });
  }

  // ── Filter persistence (across navigation) ──
  _loadFiltersFromStorage() {
    try {
      const raw = sessionStorage.getItem('library.filters');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }
  _saveFiltersToStorage() {
    try {
      const payload = { ...this.filters, dateSort: this.dateSort, dateFilter: this.dateFilter };
      sessionStorage.setItem('library.filters', JSON.stringify(payload));
    } catch {}
  }

  // ── Date-range filter ──────────────────────────────────────────
  _dateRangeLabel() {
    const f = this.dateFilter;
    if (!f || f.preset === 'all') return 'All time';
    if (f.preset === 'today') return 'Today';
    if (f.preset === 'week')  return 'This week';
    if (f.preset === 'month') return 'This month';
    if (f.preset === 'custom') {
      const fmt = iso => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?';
      return `${fmt(f.from)} → ${fmt(f.to)}`;
    }
    return 'All time';
  }

  /** Compute ISO start/end for a preset using the user's local timezone. */
  _rangeForPreset(preset) {
    const now = new Date();
    if (preset === 'today') {
      const s = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      const e = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      return { from: s.toISOString(), to: e.toISOString() };
    }
    if (preset === 'week') {
      // Week starts Sunday (common US). Start of week = Sunday 00:00, end of week = Saturday 23:59:59.
      const dow = now.getDay(); // 0=Sun
      const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow, 0, 0, 0, 0);
      const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow + 6, 23, 59, 59, 999);
      return { from: weekStart.toISOString(), to: weekEnd.toISOString() };
    }
    if (preset === 'month') {
      const s = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      const e = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return { from: s.toISOString(), to: e.toISOString() };
    }
    return { from: null, to: null };
  }

  _setDatePreset(preset) {
    if (preset === 'custom') {
      // Seed the draft with current filter values or sensible defaults
      const toInputDate = iso => {
        if (!iso) return '';
        const d = new Date(iso);
        return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      };
      this.customDateDraft = {
        from: toInputDate(this.dateFilter.from),
        to: toInputDate(this.dateFilter.to) || toInputDate(new Date().toISOString()),
      };
      this.dateFilterOpen = true;
      this.dateFilter = { ...this.dateFilter, preset: 'custom' };
      this.render();
      return;
    }
    if (preset === 'all') {
      this.dateFilter = { preset: 'all', from: null, to: null };
    } else {
      const r = this._rangeForPreset(preset);
      this.dateFilter = { preset, from: r.from, to: r.to };
    }
    this.dateFilterOpen = false;
    this._saveFiltersToStorage();
    this.loadLibrary();
  }

  _applyCustomDate() {
    const { from, to } = this.customDateDraft;
    if (!from || !to) return;
    const [fy, fm, fd] = from.split('-').map(Number);
    const [ty, tm, td] = to.split('-').map(Number);
    const fromIso = new Date(fy, fm-1, fd, 0, 0, 0, 0).toISOString();
    const toIso = new Date(ty, tm-1, td, 23, 59, 59, 999).toISOString();
    this.dateFilter = { preset: 'custom', from: fromIso, to: toIso };
    this.dateFilterOpen = false;
    this._saveFiltersToStorage();
    this.loadLibrary();
  }

  _renderDateFilter() {
    const active = this.dateFilter.preset !== 'all';
    const presets = [
      { key: 'all',    label: 'All time' },
      { key: 'today',  label: 'Today' },
      { key: 'week',   label: 'This week' },
      { key: 'month',  label: 'This month' },
      { key: 'custom', label: 'Custom range…' },
    ];
    const cd = this.customDateDraft;
    return `
      <div class="filter-multiselect" id="filter-date-wrap" data-testid="filter-date">
        <button type="button" class="filter-select filter-multiselect__btn ${active ? 'is-active' : ''}" id="filter-date-btn" aria-expanded="${this.dateFilterOpen}">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity:.7"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/></svg>
          <span>${escapeHtml(this._dateRangeLabel())}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;opacity:.6"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
        ${this.dateFilterOpen ? `
          <div class="filter-multiselect__panel" id="filter-date-panel" role="listbox" style="min-width:220px">
            ${presets.map(p => {
              const on = this.dateFilter.preset === p.key;
              return `<button type="button" class="filter-multiselect__opt ${on ? 'is-selected' : ''}" data-date-preset="${p.key}">
                <span class="filter-multiselect__check">${on ? '✓' : ''}</span>
                <span>${escapeHtml(p.label)}</span>
              </button>`;
            }).join('')}
            ${this.dateFilter.preset === 'custom' ? `
              <div class="filter-multiselect__divider"></div>
              <div class="filter-date-custom" style="padding:10px 12px;display:flex;flex-direction:column;gap:8px">
                <label style="display:flex;flex-direction:column;gap:3px;font-size:11px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.04em">
                  Start
                  <input type="date" id="custom-date-from" value="${escapeHtml(cd.from)}" style="padding:5px 8px;border:1px solid var(--border);border-radius:6px;background:var(--surface-2);color:var(--text);font-size:13px" />
                </label>
                <label style="display:flex;flex-direction:column;gap:3px;font-size:11px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.04em">
                  End
                  <input type="date" id="custom-date-to" value="${escapeHtml(cd.to)}" style="padding:5px 8px;border:1px solid var(--border);border-radius:6px;background:var(--surface-2);color:var(--text);font-size:13px" />
                </label>
                <button type="button" class="btn btn-primary btn-sm" data-action="apply-custom-date" style="margin-top:4px">Apply</button>
              </div>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `;
  }

  // ── Sort helpers ──
  /**
   * Apply the Date column sort to this.items (respecting the campaign toggle).
   * Sort key: scheduled_at if set, else created_at. dateSort='desc' (default)
   * shows newest first; 'asc' shows oldest first.
   */
  _sortedItems() {
    const base = this.showCampaignPosts ? this.items : this.items.filter(i => !i.root_id && !i.parent_id);
    const dir = this.dateSort === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => {
      const ta = new Date(a.scheduled_at || a.created_at || 0).getTime();
      const tb = new Date(b.scheduled_at || b.created_at || 0).getTime();
      return (ta - tb) * dir;
    });
  }

  // ── Multi-select filter helpers (shared by platform + status) ──
  _platformOptions() {
    return [
      { key: 'facebook',  label: 'Facebook'  },
      { key: 'instagram', label: 'Instagram' },
      { key: 'x',         label: 'X (Twitter)' },
      { key: 'linkedin',  label: 'LinkedIn'  },
      { key: 'blog',      label: 'WordPress' },
      { key: 'website',   label: 'Website'   },
      { key: 'tiktok',    label: 'TikTok'    },
      { key: 'youtube',   label: 'YouTube'   },
      { key: 'pinterest', label: 'Pinterest' },
      { key: 'reddit',    label: 'Reddit'    }
    ];
  }
  _statusOptions() {
    return [
      { key: 'draft',     label: 'Draft' },
      { key: 'review',    label: 'In Review' },
      { key: 'approved',  label: 'Approved' },
      { key: 'scheduled', label: 'Scheduled' },
      { key: 'published', label: 'Published' }
    ];
  }
  _selectedPlatforms() {
    const t = this.filters.type;
    if (!t || t === 'all') return [];
    return Array.isArray(t) ? t : [t];
  }
  _selectedStatuses() {
    const s = this.filters.status;
    if (!s || s === 'all') return [];
    return Array.isArray(s) ? s : [s];
  }

  /**
   * Render a dropdown-style multi-select filter. Selected options are
   * highlighted (accent background + checkmark) inside the dropdown,
   * matching the native <select> look but allowing multiple picks.
   */
  _renderMultiSelectFilter({ id, label, options, selected, allLabel, open }) {
    const labelMap = Object.fromEntries(options.map(o => [o.key, o.label]));
    let buttonLabel;
    if (selected.length === 0) buttonLabel = allLabel;
    else if (selected.length === 1) buttonLabel = labelMap[selected[0]] || selected[0];
    else buttonLabel = `${selected.length} selected`;

    return `
      <div class="filter-multiselect" id="${id}-wrap" data-testid="${id}">
        <!-- Hidden native select kept in sync with the multi-select panel so
             tests (and assistive tech) can programmatically pick a value via
             locator('#${id}').selectOption(...). -->
        <select id="${id}" class="filter-multiselect__native" data-ms-native="${id}" aria-hidden="true" tabindex="-1" style="position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;overflow:hidden;">
          <option value="">${escapeHtml(allLabel)}</option>
          ${options.map(o => `<option value="${escapeHtml(o.key)}" ${selected.includes(o.key) ? 'selected' : ''}>${escapeHtml(o.label)}</option>`).join('')}
        </select>
        <button type="button" class="filter-select filter-multiselect__btn" id="${id}-btn" aria-expanded="${open ? 'true' : 'false'}">
          <span>${escapeHtml(buttonLabel)}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;opacity:.6"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
        ${open ? `
          <div class="filter-multiselect__panel" id="${id}-panel" role="listbox" aria-multiselectable="true">
            <button type="button" class="filter-multiselect__opt filter-multiselect__opt--all ${selected.length === 0 ? 'is-selected' : ''}" data-ms-all="${id}">
              <span class="filter-multiselect__check">${selected.length === 0 ? '✓' : ''}</span>
              <span>${escapeHtml(allLabel)}</span>
            </button>
            <div class="filter-multiselect__divider"></div>
            ${options.map(o => {
              const isOn = selected.includes(o.key);
              return `
                <button type="button" role="option" aria-selected="${isOn}" class="filter-multiselect__opt ${isOn ? 'is-selected' : ''}" data-ms-target="${id}" data-ms-value="${escapeHtml(o.key)}">
                  <span class="filter-multiselect__check">${isOn ? '✓' : ''}</span>
                  <span>${escapeHtml(o.label)}</span>
                </button>
              `;
            }).join('')}
          </div>
        ` : ''}
      </div>
    `;
  }

  _renderPlatformFilter() {
    return this._renderMultiSelectFilter({
      id: 'filter-platform',
      label: 'Platform',
      options: this._platformOptions(),
      selected: this._selectedPlatforms(),
      allLabel: 'All Platforms',
      open: this.platformFilterOpen
    });
  }
  _renderStatusFilter() {
    return this._renderMultiSelectFilter({
      id: 'filter-status',
      label: 'Status',
      options: this._statusOptions(),
      selected: this._selectedStatuses(),
      allLabel: 'All Statuses',
      open: this.statusFilterOpen
    });
  }

  async loadLibrary() {
    this.loading = true;
    this.render();

    try {
      const apiParams = { ...this.filters };
      // Merge date range filter into API params (don't leak our internal dateFilter shape)
      delete apiParams.dateFilter;
      if (this.dateFilter && this.dateFilter.preset !== 'all') {
        if (this.dateFilter.from) apiParams.from = this.dateFilter.from;
        if (this.dateFilter.to) apiParams.to = this.dateFilter.to;
      }
      const data = await client.getContentLibrary(apiParams);
      this.items = this._parseMedia(data.items || []);
    } catch (err) {
      console.error("[LIBRARY_LOAD_FAIL]", err);
      this.error = `Failed to load library: ${err.message}`;
    } finally {
      this.loading = false;
      this.render();
      await this.maybeAutoOpenDrawer();
    }
  }

  async maybeAutoOpenDrawer() {
    const openId = sessionStorage.getItem("library:openItemId");
    const flag = sessionStorage.getItem("library:autoOpen");
    if (!openId || !flag) {
      // Simple backwards compatibility for library:openItemId alone if flag is missing
      const simpleId = sessionStorage.getItem("library:openItemId");
      if (simpleId && !flag) {
        sessionStorage.removeItem("library:openItemId");
        this.openDrawer(simpleId);
      }
      return;
    }

    // Attempt 1: open immediately
    const ok1 = await this.tryOpenDrawerById(openId);
    if (ok1) {
      sessionStorage.removeItem("library:openItemId");
      sessionStorage.removeItem("library:autoOpen");
      return;
    }

    // Attempt 2: refresh list once and retry
    // To avoid infinite recursion, we check if we're already retrying
    if (this._isAutoOpening) return;
    this._isAutoOpening = true;

    try {
      // Re-fetch items (covers cases where item was just created but not yet in view)
      const data = await client.getContentLibrary(this.filters);
      this.items = this._parseMedia(data.items || []);
      this.render();

      const ok2 = await this.tryOpenDrawerById(openId);
      if (ok2) {
        sessionStorage.removeItem("library:openItemId");
        sessionStorage.removeItem("library:autoOpen");
        return;
      }
    } catch (e) {
      console.error("[AUTO_OPEN_RETRY_FAIL]", e);
    } finally {
      this._isAutoOpening = false;
    }

    // If still not found, clear keys and alert
    sessionStorage.removeItem("library:openItemId");
    sessionStorage.removeItem("library:autoOpen");
    toast('warning', "Could not open item in Library. It may be filtered out or missing.");
  }

  async tryOpenDrawerById(openId) {
    try {
      if (typeof this.openDrawer === "function") {
        this.openDrawer(openId);
        return this.drawerOpen && String(this.selectedItemId) === String(openId);
      }
    } catch (e) { }
    return false;
  }

  openDrawer(itemId) {
    const item = this.items.find(i => String(i.id) === String(itemId));
    if (!item) return;

    // Blog posts live in the blog_posts table and have rich content
    // (WYSIWYG, images, snippets). Route them to the full blog editor
    // rather than the simple library drawer which can't handle HTML.
    if (isBlogItem(item)) {
      window.location.hash = `#create?mode=blog&blogId=${encodeURIComponent(item.id)}`;
      return;
    }

    this.selectedItemId = itemId;
    this.drawerOpen = true;
    this.draftText = item.content || "";
    this.draftTitle = item.title || "";
    this.draftScheduledAt = item.scheduled_at || "";
    this.draftMedia = Array.isArray(item.media) ? [...item.media] : [];
    this.render();

    // Focus textarea after render
    setTimeout(() => {
      this.container.querySelector("#edit-content")?.focus();
    }, 50);
  }

  closeDrawer() {
    this.drawerOpen = false;
    this.selectedItemId = null;
    this.render();
  }

  render() {
    let content = "";

    if (this.loading) {
      content = `
        <div class="library-page" style="padding: 24px;">
          <div class="skeleton skeleton-title"></div>
          <div class="skeleton skeleton-text short"></div>
          <div style="display:flex;gap:12px;margin:20px 0;">
            <div class="skeleton" style="height:36px;width:200px;"></div>
            <div class="skeleton" style="height:36px;width:140px;"></div>
            <div class="skeleton" style="height:36px;width:140px;"></div>
          </div>
          <div class="skeleton-grid">
            <div class="skeleton skeleton-card"></div>
            <div class="skeleton skeleton-card"></div>
            <div class="skeleton skeleton-card"></div>
            <div class="skeleton skeleton-card"></div>
            <div class="skeleton skeleton-card"></div>
            <div class="skeleton skeleton-card"></div>
          </div>
        </div>
      `;
    } else if (this.error) {
      content = `
        <div class="error-banner">
          <p>${escapeHtml(this.error)}</p>
          <button class="btn btn-glass btn-sm" id="retry-btn">Retry</button>
        </div>
      `;
    } else {
      content = `
      <div class="library-page" id="page-library" data-testid="library-page">
        ${renderPageHeader({
          eyebrow: "Content Archive",
          title: "Library",
          subtitle: "Every post, draft, and asset in one vault.",
          tourPlaylist: 'library',
          rightHtml: `<span id="library-failed-badge"></span>`
        })}

        <div class="library-container page-shell page-content">
          <div class="library-toolbar">
            <input type="text" class="library-search" id="library-search" placeholder="Search content..." value="${escapeHtml(this.filters.search || '')}">
            ${this._renderStatusFilter()}
            ${this._renderPlatformFilter()}
            ${this._renderDateFilter()}
            <div class="view-toggle">
              <button class="view-toggle-btn ${this.viewMode !== 'list' ? 'active' : ''}" data-view="grid" title="Grid view">⊞</button>
              <button class="view-toggle-btn ${this.viewMode === 'list' ? 'active' : ''}" data-view="list" title="List view">☰</button>
            </div>
            <button class="btn btn-ghost btn-sm ${this.showCampaignPosts ? 'active' : ''}" data-action="toggle-campaigns" title="${this.showCampaignPosts ? 'Hide' : 'Show'} campaign posts" style="margin-left:auto;font-size:12px;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:4px;"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
              ${this.showCampaignPosts ? 'Hide Campaigns' : 'Show Campaigns'}
            </button>
            <button class="btn btn-ghost btn-sm" data-action="open-trash" title="Recently Deleted" style="font-size:12px;opacity:0.7;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:4px;"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              Trash
            </button>
          </div>
          ${this.items.length === 0 ? `
            <div class="empty-state">
              <div class="empty-state-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color:var(--primary,#8B5CF6);opacity:0.6;"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></div>
              <div class="empty-state-title">Your content library is empty</div>
              <div class="empty-state-text">The fastest path: ask Heist's chat. It plans, drafts, and saves everything to your library in one conversation.</div>
              <div class="empty-state-actions">
                <a href="#memory?tab=chat&prompt=Write%20me%20a%20week%20of%20social%20posts%20for%20my%20brand" class="btn btn-primary" data-testid="empty-talk-to-heist">Talk to Heist &rarr;</a>
                <a href="#create" class="btn btn-secondary">Open Content Studio</a>
                <button class="btn btn-secondary" data-action="open-gmw">Generate Your Month</button>
              </div>
            </div>
          ` : this.viewMode === 'list' ? `
            ${this.selectedIds.size > 0 ? `
              <div class="ll-bulk-bar" data-testid="ll-bulk-bar">
                <label class="ll-bulk-check">
                  <input type="checkbox" class="ll-select-all" ${this.selectedIds.size === this.items.length ? 'checked' : ''} />
                  <span>${this.selectedIds.size} selected</span>
                </label>
                <div class="ll-bulk-actions">
                  <select class="ll-bulk-select" data-bulk="platform">
                    <option value="">Set Platform...</option>
                    <option value="facebook">Facebook</option>
                    <option value="instagram">Instagram</option>
                    <option value="x">X</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="tiktok">TikTok</option>
                    <option value="youtube">YouTube</option>
                    <option value="pinterest">Pinterest</option>
                    <option value="reddit">Reddit</option>
                  </select>
                  <select class="ll-bulk-select" data-bulk="status">
                    <option value="">Set Status...</option>
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="published">Published</option>
                  </select>
                  <button class="btn btn-sm btn-danger ll-bulk-delete" data-action="bulk-delete">Delete</button>
                  <button class="btn btn-sm btn-ghost" data-action="clear-selection">Clear</button>
                </div>
              </div>
            ` : ''}
            <div class="library-list" data-testid="library-list">
              <div class="library-list-header">
                <span class="ll-col ll-col--check"><input type="checkbox" class="ll-select-all" ${this.selectedIds.size === this.items.length && this.items.length > 0 ? 'checked' : ''} /></span>
                <span class="ll-col ll-col--content">Content</span>
                <span class="ll-col ll-col--platform">Platform</span>
                <span class="ll-col ll-col--status">Status</span>
                <button type="button" class="ll-col ll-col--date ll-col--sortable" id="library-sort-date" title="Sort by date" aria-label="Sort by date ${this.dateSort === 'asc' ? 'descending' : 'ascending'}">
                  Date
                  <span class="ll-sort-arrow" aria-hidden="true">${this.dateSort === 'asc' ? '▲' : '▼'}</span>
                </button>
                <span class="ll-col ll-col--actions"></span>
              </div>
              ${this._sortedItems().map(item => this.renderListItem(item)).join('')}
            </div>
          ` : `
            <div class="library-grid" data-testid="library-grid">
              ${this._sortedItems().map(item => this.renderCard(item)).join('')}
            </div>
          `}
          ${this.selectedIds.size >= 2 ? `
            <div class="library-selection-bar">
              <span>${this.selectedIds.size} items selected</span>
              <button class="btn btn-primary btn-sm" data-action="repurpose-to-blog" ${this.repurposing ? 'disabled' : ''}>
                ${this.repurposing ? 'Creating Blog...' : 'Create Blog from Selected'}
              </button>
              <button class="btn btn-ghost btn-sm" data-action="clear-selection">Clear</button>
            </div>
          ` : ''}
        </div>

        ${this.drawerOpen ? this.renderEditorDrawer() : ""}
      `;
    }

    this.container.innerHTML = content;

    this.bindEvents();
    this._mountDrawerToolbar();
    this._mountFailedDrawer();

    if (this.error) {
      this.container.querySelector("#retry-btn")?.addEventListener("click", () => {
        this.error = null;
        this.loadLibrary();
      });
    }
  }

  _updateDrawerPreview() {
    const previewCont = this.container.querySelector("#drawer-preview");
    if (!previewCont) return;
    const item = this.items.find(i => String(i.id) === String(this.selectedItemId));
    if (!item) return;
    previewCont.innerHTML = renderPreview(
      item.platform,
      this.draftText,
      this.draftTitle,
      window.__auth__?.name || "Your Brand",
      this.draftMedia
    );
    updateCharCounter('lib-char-counter', (this.draftText || '').length);
  }

  _mountDrawerToolbar() {
    if (!this.drawerOpen) return;
    const toolbar = this.container.querySelector('.assist-toolbar');
    const aiBar = this.container.querySelector('.ai-bar--complete');
    if (!toolbar && !aiBar) return;

    const item = this.items.find(i => String(i.id) === String(this.selectedItemId));
    const getContent = () => this.container.querySelector('#edit-content')?.value || '';
    const setContent = (text) => {
      const el = this.container.querySelector('#edit-content');
      if (el) el.value = text;
      this.draftText = text;
      if (item) item.content = text;
      // Live-update the preview so user sees AI changes immediately
      this._updateDrawerPreview();
    };
    const onToast = (msg, kind) => toast(kind, msg);
    const platform = item?.platform || 'general';

    const handler = async (e) => {
      await handleAiAssistClick(e, getContent, setContent, platform, onToast);
    };

    if (toolbar) toolbar.addEventListener('click', handler);
    if (aiBar) aiBar.addEventListener('click', handler);
  }

  _mountFailedDrawer() {
    const anchor = this.container.querySelector('#library-failed-badge');
    if (!anchor) return;

    if (!this._failedDrawer) {
      this._failedDrawer = new FailedPostsDrawer({
        getBadgeAnchor: () => this.container.querySelector('#library-failed-badge'),
        toast: (type, msg) => toast(type, msg),
        confirm: (opts) => confirmModal({
          ...opts,
          confirmLabel: 'Confirm',
          danger: false
        }),
        onRetry: async () => {},
        onCancel: async () => {},
      });
      this._failedDrawer.init();
    } else {
      // Badge anchor was replaced by innerHTML — reattach
      this._failedDrawer._ensureBadge();
      this._failedDrawer._updateBadge();
    }
  }

  renderCard(item) {
    const snippet = item.content.length > 120 ? item.content.substring(0, 117) + "..." : item.content;
    // Show scheduled date if set, fall back to created date. Matches Calendar view.
    const date = formatDateMDY(item.scheduled_at || item.created_at);
    const status = item.status || (item.scheduled_at ? 'scheduled' : 'draft');
    const typeIcon = isBlogItem(item) ? '📄' : '📱';
    const typeLabel = isBlogItem(item) ? 'Article' : 'Social';
    const platformName = item.platform.charAt(0).toUpperCase() + item.platform.slice(1);

    // Platform pill — handles both 'x' and 'twitter' stored values
    const platMap = {
      facebook:  { cls: 'pp-fb',   label: 'Facebook'  },
      instagram: { cls: 'pp-ig',   label: 'Instagram'  },
      x:         { cls: 'pp-x',    label: 'X'          },
      twitter:   { cls: 'pp-x',    label: 'X'          },
      linkedin:  { cls: 'pp-li',   label: 'LinkedIn'   },
      blog:      { cls: 'pp-blog', label: 'WordPress'  },
      website:   { cls: 'pp-website', label: 'Website' },
      tiktok:    { cls: 'pp-tt',   label: 'TikTok'     },
      youtube:   { cls: 'pp-yt',   label: 'YouTube'    },
      pinterest: { cls: 'pp-pin',  label: 'Pinterest'  },
      reddit:    { cls: 'pp-rd',   label: 'Reddit'     },
    };
    const plat = platMap[item.platform] || { cls: 'pp-fb', label: item.platform ? item.platform.charAt(0).toUpperCase() + item.platform.slice(1) : 'Social' };
    const pill = `<span class="pp ${plat.cls}"><span class="pp-dot"></span>${plat.label}</span>`;

    // Status badge
    const statusMap = {
      scheduled:        { cls: 'sb-b sb-sched',  dot: true,  label: 'Scheduled' },
      published:        { cls: 'sb-b sb-pub',    dot: true,  label: 'Published' },
      failed:           { cls: 'sb-b sb-fail',   dot: false, label: 'Failed'    },
      dead_letter:      { cls: 'sb-b sb-fail',   dot: false, label: 'Failed'    },
      active:           { cls: 'sb-b sb-active', dot: true,  label: 'Active'    },
      draft:            { cls: 'sb-b sb-draft',  dot: false, label: 'Draft'     },
      pending_approval: { cls: 'sb-b sb-pending-review', dot: true, label: 'Pending Review' },
    };
    const st = statusMap[(item.status || 'draft').toLowerCase()]
            || { cls: 'sb-b sb-draft', dot: false, label: 'Draft' };
    const badge = `<span class="${st.cls}">${st.dot ? '<span class="sb-dot"></span>' : ''}${st.label}</span>`;

    let perfScore = null;
    try { perfScore = item.performance_signal ? JSON.parse(item.performance_signal).score : null; } catch(e) {}
    const isChecked = this.selectedIds.has(item.id);

    return `
      <div class="library-card ${isChecked ? 'library-card--selected' : ''}" data-id="${item.id}" data-testid="library-card" data-card-platform="${item.platform}">
        <label class="library-card-checkbox" onclick="event.stopPropagation()">
          <input type="checkbox" class="select-item-cb" data-select-id="${item.id}" ${isChecked ? 'checked' : ''} />
        </label>
        <div class="library-card-media library-card-preview">
          ${renderPreview(item.platform, item.content, item.title, window.__auth__?.name || 'Your Brand', item.media)}
        </div>
        <div class="card-body" style="padding: 12px 14px;">
          <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 6px;">
            <select class="card-inline-select card-inline-platform" data-card-field="platform" data-card-id="${item.id}">
              ${['facebook','instagram','x','linkedin','tiktok','youtube','pinterest','reddit'].map(p => `<option value="${p}" ${item.platform === p ? 'selected' : ''}>${p === 'x' ? 'X' : p.charAt(0).toUpperCase() + p.slice(1)}</option>`).join('')}
            </select>
            <select class="card-inline-select card-inline-status" data-card-field="status" data-card-id="${item.id}">
              ${['draft','scheduled','published'].map(s => `<option value="${s}" ${(item.status || 'draft') === s ? 'selected' : ''}>${s.charAt(0).toUpperCase() + s.slice(1)}</option>`).join('')}
            </select>
          </div>
          <input class="card-inline-title" data-card-field="title" data-card-id="${item.id}" value="${escapeHtml(item.title || (platformName + ' Post'))}" />
        </div>
        <div class="library-card-footer" style="padding: 8px 14px;">
          <div class="content-type-indicator">${typeIcon} ${typeLabel} · ${item.scheduled_at ? formatDateTimeMDY12h(item.scheduled_at) : date}</div>
          <div class="library-card-act-row">
            <div class="perf-signal-btns">
              <button class="perf-btn ${perfScore === 'high' ? 'perf-btn--active-high' : ''}" data-perf-id="${item.id}" data-score="high" title="High performer">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              </button>
              <button class="perf-btn ${perfScore === 'low' ? 'perf-btn--active-low' : ''}" data-perf-id="${item.id}" data-score="low" title="Low performer">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>
              </button>
            </div>
            <button class="lib-act-btn btn-preview" data-id="${item.id}" title="Edit">
              <svg viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
            <button class="lib-act-btn lib-act-btn--danger btn-delete" data-id="${item.id}" title="Delete">
              <svg viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
            </button>
          </div>
        </div>
        ${(['draft', 'generated'].includes(item.status || 'draft')) && window.__auth__?.workspace_id && window.__auth__?.workspace_role === 'editor' ? `
        <div class="card-review-action" style="padding: 0 14px 10px;">
          <button class="btn-submit-review" data-action="submit-approval" data-content-id="${item.id}">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            Submit for Review
          </button>
        </div>
        ` : ''}
      </div>
      `;
  }

  renderListItem(item) {
    var snippet = item.content.length > 100 ? item.content.substring(0, 97) + '...' : item.content;
    // Show scheduled date if set, fall back to created date. Matches Calendar view.
    var date = formatDateMDY(item.scheduled_at || item.created_at);
    var status = item.status || (item.scheduled_at ? 'scheduled' : 'draft');
    var isChecked = this.selectedIds.has(item.id);
    var isEditing = (this._editingRowId === item.id || this._editingRowId === String(item.id)) ? this._editingCell : false;
    let perfScore = null;
    try { perfScore = item.performance_signal ? JSON.parse(item.performance_signal).score : null; } catch(e) {}

    var platMap = {
      facebook: { cls: 'pp-fb', label: 'Facebook' },
      instagram: { cls: 'pp-ig', label: 'Instagram' },
      x: { cls: 'pp-x', label: 'X' },
      twitter: { cls: 'pp-x', label: 'X' },
      linkedin: { cls: 'pp-li', label: 'LinkedIn' },
      blog: { cls: 'pp-blog', label: 'WordPress' },
      website: { cls: 'pp-website', label: 'Website' },
      tiktok: { cls: 'pp-tt', label: 'TikTok' },
      youtube: { cls: 'pp-yt', label: 'YouTube' },
      pinterest: { cls: 'pp-pin', label: 'Pinterest' },
      reddit: { cls: 'pp-rd', label: 'Reddit' }
    };
    var plat = platMap[item.platform] || { cls: 'pp-fb', label: item.platform ? item.platform.charAt(0).toUpperCase() + item.platform.slice(1) : 'Social' };

    var statusMap = {
      scheduled: { cls: 'sb-b sb-sched', label: 'Scheduled' },
      published: { cls: 'sb-b sb-pub', label: 'Published' },
      failed: { cls: 'sb-b sb-fail', label: 'Failed' },
      dead_letter: { cls: 'sb-b sb-fail', label: 'Failed' },
      active: { cls: 'sb-b sb-active', label: 'Active' },
      draft: { cls: 'sb-b sb-draft', label: 'Draft' }
    };
    var st = statusMap[(status).toLowerCase()] || { cls: 'sb-b sb-draft', label: 'Draft' };

    // Seamless inline editing — each cell is always in-place editable
    var editingContent = isEditing === 'content';
    var editingPlatform = isEditing === 'platform';
    var editingStatus = isEditing === 'status';
    var editingDate = isEditing === 'date';
    // For date input: convert to YYYY-MM-DD
    var rawDate = item.scheduled_at || item.created_at || '';
    var dateVal = '';
    try { dateVal = rawDate ? new Date(rawDate).toISOString().split('T')[0] : ''; } catch(e) {}

    return `
      <div class="library-list-row ${isChecked ? 'll-row--selected' : ''}" data-id="${item.id}" data-testid="library-card" data-card-platform="${item.platform}">
        <div class="ll-col ll-col--check"><input type="checkbox" class="ll-row-cb" data-select-id="${item.id}" ${isChecked ? 'checked' : ''} /></div>
        <div class="ll-col ll-col--content">
          <div class="ll-content-editable ll-editable" data-edit-cell="content" data-edit-row="${item.id}">
            ${editingContent
              ? `<input class="ll-inline-input ll-inline-input--title" data-field="content" data-id="${item.id}" value="${escapeHtml(item.content).replace(/"/g, '&quot;')}" />`
              : `<span class="ll-title">${escapeHtml(item.title || (plat.label + ' Post'))}</span>
                 <span class="ll-snippet">${escapeHtml(snippet)}</span>`
            }
          </div>
        </div>
        <div class="ll-col ll-col--platform ll-editable" data-edit-cell="platform" data-edit-row="${item.id}">
          ${editingPlatform
            ? `<select class="ll-inline-select" data-field="platform" data-id="${item.id}">
                <option value="facebook" ${item.platform === 'facebook' ? 'selected' : ''}>Facebook</option>
                <option value="instagram" ${item.platform === 'instagram' ? 'selected' : ''}>Instagram</option>
                <option value="x" ${item.platform === 'x' ? 'selected' : ''}>X</option>
                <option value="linkedin" ${item.platform === 'linkedin' ? 'selected' : ''}>LinkedIn</option>
                <option value="blog" ${item.platform === 'blog' ? 'selected' : ''}>WordPress</option>
                <option value="website" ${item.platform === 'website' ? 'selected' : ''}>Website</option>
                <option value="tiktok" ${item.platform === 'tiktok' ? 'selected' : ''}>TikTok</option>
                <option value="youtube" ${item.platform === 'youtube' ? 'selected' : ''}>YouTube</option>
                <option value="pinterest" ${item.platform === 'pinterest' ? 'selected' : ''}>Pinterest</option>
                <option value="reddit" ${item.platform === 'reddit' ? 'selected' : ''}>Reddit</option>
              </select>`
            : `<span class="pp ${plat.cls}"><span class="pp-dot"></span>${plat.label}</span>`
          }
        </div>
        <div class="ll-col ll-col--status ll-editable" data-edit-cell="status" data-edit-row="${item.id}">
          ${editingStatus
            ? `<select class="ll-inline-select" data-field="status" data-id="${item.id}">
                <option value="draft" ${status === 'draft' ? 'selected' : ''}>Draft</option>
                <option value="scheduled" ${status === 'scheduled' ? 'selected' : ''}>Scheduled</option>
                <option value="published" ${status === 'published' ? 'selected' : ''}>Published</option>
              </select>`
            : `<span class="${st.cls}">${st.label}</span>`
          }
        </div>
        <div class="ll-col ll-col--date ll-editable" data-edit-cell="date" data-edit-row="${item.id}">
          ${editingDate
            ? `<input type="datetime-local" class="ll-inline-input ll-inline-date" data-field="scheduled_at" data-id="${item.id}" value="${dateVal}" />`
            : `${date}`
          }
        </div>
        <div class="ll-col ll-col--actions">
          <button class="perf-btn perf-btn-sm ${perfScore === 'high' ? 'perf-btn--active-high' : ''}" data-perf-id="${item.id}" data-score="high" title="High performer">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
          </button>
          <button class="perf-btn perf-btn-sm ${perfScore === 'low' ? 'perf-btn--active-low' : ''}" data-perf-id="${item.id}" data-score="low" title="Low performer">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>
          </button>
          <button class="lib-act-btn lib-act-btn--danger btn-delete" data-id="${item.id}" title="Delete">
            <svg viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
          </button>
        </div>
      </div>
    `;
  }

  renderEditorDrawer() {
    const item = this.items.find(i => String(i.id) === String(this.selectedItemId));
    if (!item) return "";

    const createdDate = formatDateMDY(item.created_at);
    const showTitle = isBlogItem(item) || (item.title && item.title !== "");

    return `
      < div class="drawer-backdrop" data - action="close-drawer" id = "drawer-backdrop" ></div >
        <aside class="edit-drawer open" role="dialog" aria-modal="true" data-testid="library-edit-drawer">
          <div class="drawer-header">
            <div class="drawer-title">
              <h3>Edit Draft</h3>
              <span class="pill pill-platform">${item.platform.toUpperCase()}</span>
              <span class="pill pill-status">${item.status}</span>
              <div id="save-status-area" class="save-status-area"></div>
            </div>
            <div class="drawer-header-actions">
              <button class="icon-btn" data-action="delete-item" title="Delete Item" aria-label="Delete item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
              </button>
              <button class="icon-btn" data-action="close-drawer" title="Close Drawer" aria-label="Close drawer">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>

          <div class="drawer-body">
            <!-- Two-pane layout with dedicated scroll areas -->
            <section class="drawer-editor">
              <div class="pane-scroll-area">
                <div class="editor-settings">
                  ${showTitle ? `
                  <div class="setting-item">
                    <label for="draft-title">Title</label>
                    <input type="text" id="draft-title" placeholder="Post title..." value="${escapeHtml(this.draftTitle)}" />
                  </div>
                ` : ''}
                  <label for="draft-scheduled-at">Scheduled At</label>
                  <input type="datetime-local" id="draft-scheduled-at" value="${toDatetimeLocalValue(this.draftScheduledAt)}" />
                  <div id="draft-scheduled-preview" class="input-helper">${this.draftScheduledAt ? `Display: ${formatDateTimeMDY12h(this.draftScheduledAt)}` : ""}</div>
                </div>

                <div class="editor-main">
                  <div class="editor-toolbar">
                    <span class="muted">Content</span>
                    <button class="btn btn-secondary btn-sm" data-action="copy-text">Copy</button>
                  </div>
                  ${renderAiAssistToolbar({ showFillAi: true, showFillToAll: false })}
                  <textarea id="edit-content" data-testid="library-edit-content" placeholder="Content text...">${this.draftText}</textarea>
                  ${renderCharCounter({ platform: item.platform, initialLength: this.draftText.length, counterId: 'lib-char-counter' })}
                </div>

                <div class="editor-media" style="margin-top: 12px;">
                  ${renderMediaZone(this.draftMedia, 'drawer-media-input')}
                  ${window.__auth__?.workspace_id ? '<button class="media-library-btn" id="lib-open-media-library">Browse Media Library</button>' : ''}
                </div>

                <details class="drawer-details">
                  <summary>Details</summary>
                  <ul class="meta-list">
                    <li><strong>ID:</strong> <span class="small-id">${item.id}</span></li>
                    <li><strong>Created:</strong> ${createdDate}</li>
                    <li><strong>Type:</strong> ${item.type}</li>
                  </ul>
                </details>
              </div>
            </section>

            <section class="drawer-preview">
              <div class="preview-fixed-header">
                <span>PREVIEW</span>
              </div>
              <div class="pane-scroll-area preview-scroll">
                <div class="preview-container" id="drawer-preview">
                  ${renderPreview(item.platform, this.draftText, this.draftTitle, window.__auth__?.name || "Your Brand", this.draftMedia)}
                </div>
              </div>
            </section>
          </div>

          ${(item.status === 'review' || item.status === 'approved' || item.status === 'rejected') ? `
          <div class="review-comments">
            <div class="review-comments-title">Review Thread</div>
            ${item.status === 'review' ? `
            <div class="review-comment">
              <div class="review-comment-avatar">YB</div>
              <div>
                <div class="review-comment-bubble">Submitted for team review. Awaiting feedback.</div>
                <div class="review-comment-meta">System · Just now</div>
              </div>
            </div>
            <div class="review-actions">
              <button class="btn btn-glass btn-sm" data-action="approve-item" data-id="${item.id}">Approve</button>
              <button class="btn btn-danger btn-sm" data-action="reject-item" data-id="${item.id}">Request Changes</button>
            </div>
            ` : item.status === 'approved' ? `
            <div class="review-comment">
              <div class="review-comment-avatar">TL</div>
              <div>
                <div class="review-comment-bubble">Looks great! Approved for scheduling.</div>
                <div class="review-comment-meta">Team Lead · Just now</div>
              </div>
            </div>
            ` : `
            <div class="review-comment">
              <div class="review-comment-avatar">TL</div>
              <div>
                <div class="review-comment-bubble">Needs revisions — please update the call to action.</div>
                <div class="review-comment-meta">Team Lead · Just now</div>
              </div>
            </div>
            `}
          </div>
          ` : ''}

          <div class="drawer-footer">
            <button class="btn btn-ghost" data-action="close-drawer">Close</button>
            <div class="drawer-footer-right">
              <button class="btn btn-ghost" data-action="rewrite-item" id="btn-rewrite-footer">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m16 12-4-4-4 4"/><path d="M12 16V8"/></svg>
                Update with Brain
              </button>
              <button class="btn btn-primary" data-action="save-draft" id="btn-save-edit" ${this.saveState === 'saving' ? 'disabled' : ''}>
                ${this.saveState === 'saved' ? 'Saved ✓' : (this.saveState === 'saving' ? 'Saving...' : 'Save Changes')}
              </button>
            </div>
          </div>
        </aside>
    `;
  }

  getPlatformIcon(p) {
    const icons = { facebook: "FB", instagram: "IG", x: "X", linkedin: "LI", blog: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>' };
    return icons[p] || "P";
  }

  bindEvents() {
    // Esc key to close
    document.onkeydown = (e) => {
      if (e.key === "Escape" && this.drawerOpen) {
        this.closeDrawer();
      }
    };

    // Generate Your Month from empty state
    var gmwBtn = this.container.querySelector('[data-action="open-gmw"]');
    if (gmwBtn) gmwBtn.addEventListener('click', () => openGenerateMonthWizard());

    // Trash button
    var trashBtn = this.container.querySelector('[data-action="open-trash"]');
    if (trashBtn) trashBtn.addEventListener('click', () => this._openTrash());

    // BF-3: Campaign posts toggle
    var campToggleBtn = this.container.querySelector('[data-action="toggle-campaigns"]');
    if (campToggleBtn) campToggleBtn.addEventListener('click', () => {
      this.showCampaignPosts = !this.showCampaignPosts;
      this.render();
    });

    // Inline card editing (platform, status, title)
    this.container.addEventListener('change', async (e) => {
      const el = e.target.closest('[data-card-field]');
      if (!el) return;
      e.stopPropagation();
      const id = el.dataset.cardId;
      const field = el.dataset.cardField;
      const value = el.value;
      try {
        await client.updateContentItem(id, { [field]: value });
        const item = this.items.find(i => String(i.id) === String(id));
        if (item) { item[field] = value; this.render(); }
      } catch (err) { console.error('[CARD_INLINE_EDIT]', err); toast('error', 'Couldn\'t save that change — please try again.'); }
    });
    this.container.addEventListener('blur', async (e) => {
      const el = e.target.closest('.card-inline-title');
      if (!el) return;
      const id = el.dataset.cardId;
      const value = el.value.trim();
      if (!value) return;
      try {
        await client.updateContentItem(id, { title: value });
        const item = this.items.find(i => String(i.id) === String(id));
        if (item) item.title = value;
      } catch (err) { console.error('[CARD_INLINE_EDIT]', err); toast('error', 'Couldn\'t save that change — please try again.'); }
    }, true);

    // Container Delegation
    this.container.onclick = async (e) => {
      // AI Assist toolbar in drawer
      if (this.drawerOpen) {
        const item = this.items.find(i => String(i.id) === String(this.selectedItemId));
        const handled = await handleAiAssistClick(
          e,
          () => this.container.querySelector('#edit-content')?.value || '',
          (text) => {
            const el = this.container.querySelector('#edit-content');
            if (el) el.value = text;
            this.draftText = text;
            if (item) item.content = text;
          },
          item?.platform || 'general',
          (msg, kind) => toast(kind, msg)
        );
        if (handled) return;
      }

      // Restore from trash
      const restoreBtn = e.target.closest('[data-action="restore-item"]');
      if (restoreBtn) {
        e.stopPropagation();
        const id = restoreBtn.dataset.itemId;
        if (!id) return;
        try {
          await client.restoreItem(id);
          toast('success', 'Content restored');
          this._openTrash(); // refresh trash view
        } catch(err) {
          toast('error', 'Restore failed: ' + err.message);
        }
        return;
      }

      // Submit for approval
      const submitApprovalBtn = e.target.closest('[data-action="submit-approval"]');
      if (submitApprovalBtn) {
        e.stopPropagation();
        const contentId = submitApprovalBtn.dataset.contentId;
        if (!contentId) return;
        submitApprovalBtn.disabled = true;
        submitApprovalBtn.textContent = 'Submitting...';
        try {
          await client.submitForApproval(contentId);
          toast('success', 'Submitted for review');
          await this.loadLibrary();
          this.render();
        } catch(err) {
          toast('error', err.message || 'Failed to submit');
          submitApprovalBtn.disabled = false;
          submitApprovalBtn.textContent = 'Submit for Review';
        }
        return;
      }

      // Performance signal buttons
      const perfBtn = e.target.closest('.perf-btn');
      if (perfBtn) {
        e.stopPropagation();
        const id = perfBtn.dataset.perfId;
        const score = perfBtn.dataset.score;
        if (!id || !score) return;
        perfBtn.style.opacity = '0.5';
        try {
          await client.recordPerformance(id, score);
          toast('success', score === 'high' ? 'Marked as high performer' : 'Marked as low performer');
          await this.loadLibrary();
          this.render();
        } catch(err) {
          toast('error', 'Failed: ' + err.message);
          perfBtn.style.opacity = '1';
        }
        return;
      }

      // Clicks on an ALREADY-active inline input/select should stay on that
      // control (the user is editing) — don't fall through to openDrawer.
      if (e.target.closest('.ll-inline-input, .ll-inline-select')) {
        return;
      }

      // Seamless inline cell click — activate that cell for editing.
      // The drawer opens only on clicks OUTSIDE any .ll-editable (the
      // actions column, checkbox col, row chrome, and the .btn-preview
      // pencil icon all take their own paths further down).
      const editableCell = e.target.closest('.ll-editable');
      if (editableCell && editableCell.dataset.editRow && editableCell.dataset.editCell) {
        e.stopPropagation();
        const cellField = editableCell.dataset.editCell;
        const cellRowId = editableCell.dataset.editRow;
        if (this._editingRowId === cellRowId && this._editingCell === cellField) return;
        this._editingRowId = cellRowId;
        this._editingCell = cellField;
        this.render();
        const focusEl = this.container.querySelector('.ll-inline-input, .ll-inline-select');
        if (focusEl) { focusEl.focus(); if (focusEl.select) focusEl.select(); }
        return;
      }

      // Row checkbox click
      const rowCb = e.target.closest('.ll-row-cb');
      if (rowCb) {
        e.stopPropagation();
        var id = rowCb.dataset.selectId;
        if (rowCb.checked) { this.selectedIds.add(id); } else { this.selectedIds.delete(id); }
        this.render();
        return;
      }

      const card = e.target.closest('.library-card') || e.target.closest('.library-list-row');
      const deleteBtn = e.target.closest('.btn-delete');
      const previewBtn = e.target.closest('.btn-preview');
      const actionBtn = e.target.closest('[data-action]');

      if (deleteBtn) {
        e.stopPropagation();
        this.handleDelete(deleteBtn.dataset.id);
        return;
      }

      if (previewBtn) {
        e.stopPropagation();
        this.openDrawer(previewBtn.dataset.id);
        return;
      }

      // Open drawer on card click (grid) or row click (list — non-editable area)
      if (card) {
        e.stopPropagation();
        this.openDrawer(card.dataset.id);
        return;
      }

      if (actionBtn) {
        const action = actionBtn.dataset.action;
        if (action === 'repurpose-to-blog') {
          this.handleRepurposeToBlog();
          return;
        }
        if (action === 'clear-selection') {
          this.selectedIds.clear();
          this.render();
          return;
        }
        if (action === 'close-drawer') {
          this.closeDrawer();
        } else if (action === 'delete-item') {
          this.handleDelete(this.selectedItemId);
        } else if (action === 'copy-text') {
          this.handleCopyText();
        } else if (action === 'save-draft') {
          this.handleSave();
        } else if (action === 'rewrite-item') {
          this.handleRewrite();
        } else if (action === 'submit-review') {
          const id = actionBtn.dataset.id;
          const item = this.items.find(i => String(i.id) === String(id));
          if (item) { item.status = 'review'; this.render(); }
        } else if (action === 'approve-item') {
          const id = actionBtn.dataset.id;
          const item = this.items.find(i => String(i.id) === String(id));
          if (item) { item.status = 'approved'; this.render(); }
        } else if (action === 'reject-item') {
          const id = actionBtn.dataset.id;
          const item = this.items.find(i => String(i.id) === String(id));
          if (item) { item.status = 'rejected'; this.render(); }
        }
      }
    };

    // Multi-select filters (platform + status)
    const bindMultiSelect = (btnId, wrapId, filterKey, openKey) => {
      this.container.querySelector(`#${btnId}`)?.addEventListener('click', (e) => {
        e.stopPropagation();
        // Close the other open dropdown (only one at a time)
        if (openKey === 'platformFilterOpen') this.statusFilterOpen = false;
        else this.platformFilterOpen = false;
        this[openKey] = !this[openKey];
        this.render();
      });
      // Option rows toggle
      this.container.querySelectorAll(`[data-ms-target="${wrapId}"]`).forEach(opt => {
        opt.addEventListener('click', (e) => {
          e.stopPropagation();
          const value = opt.dataset.msValue;
          const current = new Set(
            filterKey === 'type' ? this._selectedPlatforms() : this._selectedStatuses()
          );
          if (current.has(value)) current.delete(value);
          else current.add(value);
          this.filters[filterKey] = current.size === 0 ? 'all' : Array.from(current);
          this._saveFiltersToStorage();
          this.render();
          this.loadLibrary();
        });
      });
      // "All" row
      this.container.querySelector(`[data-ms-all="${wrapId}"]`)?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.filters[filterKey] = 'all';
        this._saveFiltersToStorage();
        this.render();
        this.loadLibrary();
      });
      // Hidden native <select id="{wrapId}"> — accessibility + test hook.
      // `filter.selectOption('draft')` sets a single status, empty string = 'all'.
      this.container.querySelector(`select[data-ms-native="${wrapId}"]`)?.addEventListener('change', (e) => {
        const val = e.target.value;
        this.filters[filterKey] = val === '' ? 'all' : [val];
        this._saveFiltersToStorage();
        this.render();
        this.loadLibrary();
      });
    };
    bindMultiSelect('filter-platform-btn', 'filter-platform', 'type', 'platformFilterOpen');
    bindMultiSelect('filter-status-btn', 'filter-status', 'status', 'statusFilterOpen');

    // Date filter — toggle panel + handle preset clicks + custom range apply
    this.container.querySelector('#filter-date-btn')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.statusFilterOpen = false;
      this.platformFilterOpen = false;
      this.dateFilterOpen = !this.dateFilterOpen;
      this.render();
    });
    this.container.querySelectorAll('[data-date-preset]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._setDatePreset(btn.dataset.datePreset);
      });
    });
    // Track custom date inputs as user types
    this.container.querySelector('#custom-date-from')?.addEventListener('input', (e) => {
      this.customDateDraft.from = e.target.value;
    });
    this.container.querySelector('#custom-date-to')?.addEventListener('input', (e) => {
      this.customDateDraft.to = e.target.value;
    });
    this.container.querySelector('[data-action="apply-custom-date"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this._applyCustomDate();
    });

    // Click-outside closes any open filter panel
    if ((this.platformFilterOpen || this.statusFilterOpen || this.dateFilterOpen) && !this._filterOutsideHandler) {
      this._filterOutsideHandler = (e) => {
        if (!e.target.closest('#filter-platform-wrap') && !e.target.closest('#filter-status-wrap') && !e.target.closest('#filter-date-wrap')) {
          this.platformFilterOpen = false;
          this.statusFilterOpen = false;
          this.dateFilterOpen = false;
          document.removeEventListener('click', this._filterOutsideHandler);
          this._filterOutsideHandler = null;
          this.render();
        }
      };
      setTimeout(() => document.addEventListener('click', this._filterOutsideHandler), 0);
    }
    this.container.querySelector("#library-search")?.addEventListener("input", (e) => {
      this.filters.search = e.target.value;
      this._saveFiltersToStorage();
      this.loadLibrary();
    });

    // Date column sort toggle
    this.container.querySelector('#library-sort-date')?.addEventListener('click', () => {
      this.dateSort = this.dateSort === 'desc' ? 'asc' : 'desc';
      this._saveFiltersToStorage();
      this.render(); // client-side sort — no re-fetch needed
    });
    this.container.querySelectorAll(".view-toggle-btn").forEach(btn => {
      btn.addEventListener("click", () => {
        this.viewMode = btn.dataset.view;
        this.render();
      });
    });

    // Multi-select checkboxes (grid view)
    this.container.querySelectorAll('.select-item-cb').forEach(cb => {
      cb.addEventListener('change', (e) => {
        const id = e.target.dataset.selectId;
        if (e.target.checked) {
          this.selectedIds.add(id);
        } else {
          this.selectedIds.delete(id);
        }
        this.render();
      });
    });

    // List view: Select All checkbox
    this.container.querySelectorAll('.ll-select-all').forEach(cb => {
      cb.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.items.forEach(item => this.selectedIds.add(item.id));
        } else {
          this.selectedIds.clear();
        }
        this.render();
      });
    });

    // List view: Bulk action selects
    this.container.querySelectorAll('.ll-bulk-select').forEach(sel => {
      sel.addEventListener('change', (e) => {
        var field = sel.dataset.bulk;
        var value = e.target.value;
        if (field && value) this._handleBulkUpdate(field, value);
      });
    });

    // Bulk delete
    var bulkDeleteBtn = this.container.querySelector('.ll-bulk-delete');
    if (bulkDeleteBtn) bulkDeleteBtn.addEventListener('click', () => this._handleBulkDelete());

    // Seamless inline edit: auto-save on blur/Enter for inputs, on change for selects
    this.container.querySelectorAll('.ll-inline-input').forEach(input => {
      input.addEventListener('blur', () => this._commitInlineCell(input.dataset.id, input.dataset.field, input.value));
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
        if (e.key === 'Escape') { this._editingRowId = null; this._editingCell = null; this.render(); }
      });
    });
    this.container.querySelectorAll('.ll-inline-select').forEach(sel => {
      sel.addEventListener('change', () => this._commitInlineCell(sel.dataset.id, sel.dataset.field, sel.value));
      sel.addEventListener('blur', () => { this._editingRowId = null; this._editingCell = null; this.render(); });
    });

    if (this.drawerOpen) {
      const textarea = this.container.querySelector("#edit-content");
      const titleInput = this.container.querySelector("#draft-title");
      const scheduleInput = this.container.querySelector("#draft-scheduled-at");

      const updatePreview = () => this._updateDrawerPreview();

      textarea?.addEventListener("input", (e) => {
        this.draftText = e.target.value;
        updatePreview();
      });

      titleInput?.addEventListener("input", (e) => {
        this.draftTitle = e.target.value;
        updatePreview();
      });

      scheduleInput?.addEventListener("input", (e) => {
        const val = e.target.value;
        const previewEl = this.container.querySelector("#draft-scheduled-preview");
        if (previewEl) {
          previewEl.textContent = val ? `Display: ${formatDateTimeMDY12h(val)} ` : "";
        }
      });

      scheduleInput?.addEventListener("change", (e) => {
        this.draftScheduledAt = e.target.value ? new Date(e.target.value).toISOString() : "";
      });

      // Media zone events
      bindMediaZoneEvents(this.container, this.draftMedia, (updated) => {
        this.draftMedia = updated;
        updatePreview();
        this.render();
      }, 'drawer-media-input');
    }

    // Browse Media Library button
    const mlBtn = this.container.querySelector('#lib-open-media-library');
    if (mlBtn) mlBtn.onclick = () => openMediaLibrary(this);
  }

  handleCopyText() {
    navigator.clipboard.writeText(this.draftText).then(() => {
      const btn = this.container.querySelector('[data-action="copy-text"]');
      if (btn) {
        const oldText = btn.innerText;
        btn.innerText = "Copied!";
        setTimeout(() => { if (btn) btn.innerText = oldText; }, 2000);
      }
    });
  }

  async handleSave() {
    const statusArea = this.container.querySelector("#save-status-area");

    this.saveState = "saving";
    this.render();

    try {
      const payload = {
        content: this.draftText,
        title: this.draftTitle,
        scheduled_at: this.draftScheduledAt,
        status: this.draftScheduledAt ? 'scheduled' : 'draft',
        media: this.draftMedia.length > 0 ? this.draftMedia : null
      };

      const res = await client.updateContentItem(this.selectedItemId, payload);
      if (res.ok) {
        const index = this.items.findIndex(i => String(i.id) === String(this.selectedItemId));
        if (index !== -1) {
          const merged = { ...this.items[index], ...res.data };
          // Parse media_json into media array for drawer compatibility
          if (merged.media_json) {
            try { merged.media = JSON.parse(merged.media_json); } catch (e) { merged.media = null; }
          } else if (merged.media_json === null) {
            merged.media = null;
          }
          this.items[index] = merged;
        }

        this.saveState = "idle";
        this.closeDrawer();
      }
    } catch (err) {
      console.error("[LIBRARY_SAVE_FAIL]", err);
      this.saveState = "idle";
      this.render();
      if (statusArea) {
        statusArea.innerHTML = `<span class="status-error">Error: ${escapeHtml(err.message)}</span>`;
      }
    }
  }

  async handleRewrite() {
    if (!this.selectedItemId) return;
    const footerBtn = this.container.querySelector("#btn-rewrite-footer");
    const statusArea = this.container.querySelector("#save-status-area");

    if (this.isRewriting) return;

    try {
      this.isRewriting = true;
      if (footerBtn) { footerBtn.disabled = true; footerBtn.textContent = "Updating..."; }
      if (statusArea) statusArea.innerHTML = '<span class="status-success" style="color:var(--primary-color)">Brain is thinking...</span>';

      const res = await client.rewriteContentItem(this.selectedItemId);

      if (res.ok) {
        this.draftText = res.data.content;
        const textarea = this.container.querySelector("#edit-content");
        if (textarea) textarea.value = this.draftText;

        // Update preview
        const previewCont = this.container.querySelector("#drawer-preview");
        const item = this.items.find(i => String(i.id) === String(this.selectedItemId));
        if (item && previewCont) {
          previewCont.innerHTML = renderPreview(item.platform, this.draftText, this.draftTitle, window.__auth__?.name || "Your Brand");
        }

        if (statusArea) {
          statusArea.innerHTML = '<span class="status-success">Updated with Brain ✓</span>';
          setTimeout(() => { if (statusArea) statusArea.innerHTML = ""; }, 3000);
        }
      }
    } catch (err) {
      console.error("[LIBRARY_REWRITE_FAIL]", err);
      if (statusArea) {
        // err.message usually contains RID if client formatted it
        statusArea.innerHTML = `<span class="status-error">Update failed: ${escapeHtml(err.message)}</span>`;
      }
    } finally {
      this.isRewriting = false;
      if (footerBtn) { footerBtn.disabled = false; footerBtn.textContent = "Update with Brain"; }
    }
  }

  async handleRepurposeToBlog() {
    if (this.selectedIds.size < 2 || this.repurposing) return;
    this.repurposing = true;
    this.render();

    try {
      const res = await client.repurposeToBlog([...this.selectedIds]);
      if (res.ok && res.item) {
        toast('success', 'Blog draft created from selected posts.');
        this.selectedIds.clear();
        window.location.hash = `#create?mode=blog&blogId=${encodeURIComponent(res.item.id)}`;
      }
    } catch (err) {
      console.error('[REPURPOSE_TO_BLOG_FAIL]', err);
      toast('error', `Failed to create blog: ${err.message}`);
    } finally {
      this.repurposing = false;
      this.render();
    }
  }

  async handleDelete(id) {
    if (!id) return;
    if (!await confirmModal({ title: 'Delete Item', message: 'This item will be moved to Trash and permanently deleted after 30 days.', confirmLabel: 'Delete', danger: true })) return;
    try {
      await client.deleteFromLibrary(id);
      this.items = this.items.filter(i => String(i.id) !== String(id));
      if (this.selectedItemId === id) {
        this.closeDrawer();
      } else {
        this.render();
      }
    } catch (err) {
      console.error("[LIBRARY_DELETE_FAIL]", err);
      toast('error', `Failed to delete item: ${err.message}`);
    }
  }

  async _openTrash() {
    try {
      let items = (await client.getTrash()).items || [];
      const selected = new Set();
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay modal-overlay--trash active';
      document.body.appendChild(overlay);

      const renderTrash = () => {
        const allSelected = items.length > 0 && selected.size === items.length;
        overlay.innerHTML = `
          <div class="trash-modal">
            <div class="trash-modal-header">
              <div class="trash-modal-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                <div>
                  <h3>Recently Deleted</h3>
                  <span class="trash-modal-badge">Auto-empties after 60 days</span>
                </div>
              </div>
              <button class="trash-modal-close" aria-label="Close">&times;</button>
            </div>
            ${items.length > 0 ? `
              <div class="trash-bulk-bar">
                <label class="trash-bulk-check">
                  <input type="checkbox" data-action="trash-select-all" ${allSelected ? 'checked' : ''}>
                  <span>${selected.size > 0 ? `${selected.size} selected` : 'Select all'}</span>
                </label>
                <div class="trash-bulk-actions">
                  ${selected.size > 0 ? `
                    <button class="trash-bulk-btn trash-bulk-btn--danger" data-action="trash-delete-selected">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete forever (${selected.size})
                    </button>
                  ` : `
                    <button class="trash-bulk-btn trash-bulk-btn--ghost" data-action="trash-empty-all">
                      Empty trash
                    </button>
                  `}
                </div>
              </div>
            ` : ''}
            <div class="trash-modal-body">
              ${items.length === 0 ? `
                <div class="trash-modal-empty">
                  <div class="trash-modal-empty__icon"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></div>
                  <div class="trash-modal-empty__text">Trash is empty</div>
                  <div class="trash-modal-empty__sub">Deleted items appear here for 60 days before being permanently removed.</div>
                </div>
              ` : items.map(item => {
                  const daysLeft = Math.max(0, 60 - Math.floor((Date.now() - new Date(item.deleted_at).getTime()) / (24*60*60*1000)));
                  const isSel = selected.has(item.id);
                  return `<div class="trash-row ${isSel ? 'trash-row--selected' : ''}" data-item-id="${item.id}">
                    <label class="trash-row__select">
                      <input type="checkbox" data-action="trash-row-check" data-item-id="${item.id}" ${isSel ? 'checked' : ''}>
                    </label>
                    <div class="trash-row__info">
                      <div class="trash-row__title">${escapeHtml(item.title || item.platform + ' Post')}</div>
                      <div class="trash-row__preview">${escapeHtml((item.content || '').substring(0, 100))}${(item.content||'').length > 100 ? '…' : ''}</div>
                      <div class="trash-row__meta">Permanently deleted in ${daysLeft} day${daysLeft === 1 ? '' : 's'}</div>
                    </div>
                    <div class="trash-row__actions">
                      <button class="trash-row__restore" data-action="restore-item" data-item-id="${item.id}">Restore</button>
                      <button class="trash-row__delete" data-action="trash-delete-one" data-item-id="${item.id}" title="Delete forever">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  </div>`;
                }).join('')}
            </div>
          </div>`;
        wireTrash();
      };

      const reloadTrash = async () => {
        try {
          items = (await client.getTrash()).items || [];
          // Drop selections for items no longer present
          for (const id of Array.from(selected)) {
            if (!items.find(i => String(i.id) === String(id))) selected.delete(id);
          }
        } catch (e) { items = []; }
        renderTrash();
      };

      const wireTrash = () => {
        overlay.querySelector('.trash-modal-close').onclick = () => overlay.remove();

        // Single restore
        overlay.querySelectorAll('[data-action="restore-item"]').forEach(btn => {
          btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            try {
              await client.restoreItem(btn.dataset.itemId);
              toast('success', 'Content restored');
              selected.delete(btn.dataset.itemId);
              await this.loadLibrary();
              this.render();
              await reloadTrash();
            } catch (err) { toast('error', 'Restore failed: ' + err.message); }
          });
        });

        // Single permanent delete
        overlay.querySelectorAll('[data-action="trash-delete-one"]').forEach(btn => {
          btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            if (!confirm('Permanently delete this item? This cannot be undone.')) return;
            try {
              await client.permanentDeleteItem(btn.dataset.itemId);
              toast('success', 'Deleted forever');
              selected.delete(btn.dataset.itemId);
              await reloadTrash();
            } catch (err) { toast('error', 'Delete failed: ' + err.message); }
          });
        });

        // Row checkboxes
        overlay.querySelectorAll('[data-action="trash-row-check"]').forEach(cb => {
          cb.addEventListener('change', (e) => {
            const id = cb.dataset.itemId;
            if (cb.checked) selected.add(id); else selected.delete(id);
            renderTrash();
          });
        });

        // Select all
        const selAll = overlay.querySelector('[data-action="trash-select-all"]');
        if (selAll) {
          selAll.addEventListener('change', () => {
            if (selAll.checked) items.forEach(i => selected.add(i.id));
            else selected.clear();
            renderTrash();
          });
        }

        // Bulk delete selected
        const bulkDel = overlay.querySelector('[data-action="trash-delete-selected"]');
        if (bulkDel) {
          bulkDel.addEventListener('click', async () => {
            if (selected.size === 0) return;
            if (!confirm(`Permanently delete ${selected.size} item${selected.size === 1 ? '' : 's'}? This cannot be undone.`)) return;
            try {
              const result = await client.purgeTrash(Array.from(selected));
              toast('success', `Deleted ${result.deleted || selected.size} forever`);
              selected.clear();
              await reloadTrash();
            } catch (err) { toast('error', 'Bulk delete failed: ' + err.message); }
          });
        }

        // Empty trash
        const emptyBtn = overlay.querySelector('[data-action="trash-empty-all"]');
        if (emptyBtn) {
          emptyBtn.addEventListener('click', async () => {
            if (items.length === 0) return;
            if (!confirm(`Permanently delete all ${items.length} item${items.length === 1 ? '' : 's'} in trash? This cannot be undone.`)) return;
            try {
              const result = await client.emptyTrash();
              toast('success', `Emptied trash (${result.deleted || 0})`);
              selected.clear();
              await reloadTrash();
            } catch (err) { toast('error', 'Empty trash failed: ' + err.message); }
          });
        }
      };

      overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
      renderTrash();
    } catch (err) {
      toast('error', 'Failed to load trash: ' + err.message);
    }
  }

  async _commitInlineCell(id, field, value) {
    if (!id || !field) return;
    var item = this.items.find(function(i) { return String(i.id) === String(id); });
    if (!item) return;
    // Skip if value unchanged
    if (item[field] === value) {
      this._editingRowId = null;
      this._editingCell = null;
      this.render();
      return;
    }
    var updates = {};
    updates[field] = value;
    try {
      await client.updateLibraryItem(id, updates);
      item[field] = value;
      // If scheduling a date, also update status to scheduled
      if (field === 'scheduled_at' && value) item.status = item.status === 'draft' ? 'scheduled' : item.status;
      this._editingRowId = null;
      this._editingCell = null;
      this.render();
    } catch (err) {
      toast('error', 'Save failed: ' + err.message);
      this._editingRowId = null;
      this._editingCell = null;
      this.render();
    }
  }

  async _handleBulkUpdate(field, value) {
    if (!value || this.selectedIds.size === 0) return;
    var ids = Array.from(this.selectedIds);
    var failed = 0;
    for (var k = 0; k < ids.length; k++) {
      try {
        var updates = {};
        updates[field] = value;
        await client.updateLibraryItem(ids[k], updates);
        var item = this.items.find(function(i) { return String(i.id) === String(ids[k]); });
        if (item) item[field] = value;
      } catch (err) {
        failed++;
      }
    }
    if (failed > 0) {
      toast('error', failed + ' item(s) failed to update');
    } else {
      toast('success', ids.length + ' item(s) updated');
    }
    this.selectedIds.clear();
    this.render();
  }

  async _handleBulkDelete() {
    if (this.selectedIds.size === 0) return;
    var ids = Array.from(this.selectedIds);
    if (!await confirmModal({ title: 'Delete ' + ids.length + ' Items', message: 'Are you sure you want to delete ' + ids.length + ' items? This cannot be undone.', confirmLabel: 'Delete All', danger: true })) return;
    var failed = 0;
    for (var k = 0; k < ids.length; k++) {
      try {
        await client.deleteFromLibrary(ids[k]);
        this.items = this.items.filter(function(i) { return String(i.id) !== String(ids[k]); });
      } catch (err) {
        failed++;
      }
    }
    if (failed > 0) toast('error', failed + ' item(s) failed to delete');
    else toast('success', ids.length + ' item(s) deleted');
    this.selectedIds.clear();
    this.render();
  }

  async unmount() {
    document.onkeydown = null;
    if (this._failedDrawer) {
      this._failedDrawer.destroy();
      this._failedDrawer = null;
    }
  }
}
